/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { Injectable, NgZone } from '@angular/core';
import { MediaMatcher } from './media-matcher';
import { Subject } from 'rxjs/Subject';
import { RxChain, map, startWith, takeUntil } from '@angular/cdk/rxjs';
import { coerceArray } from '@angular/cdk/coercion';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { fromEventPattern } from 'rxjs/observable/fromEventPattern';
/**
 * Utility for checking the matching state of \@media queries.
 */
var BreakpointObserver = (function () {
    /**
     * @param {?} mediaMatcher
     * @param {?} zone
     */
    function BreakpointObserver(mediaMatcher, zone) {
        this.mediaMatcher = mediaMatcher;
        this.zone = zone;
        /**
         * A map of all media queries currently being listened for.
         */
        this._queries = new Map();
        /**
         * A subject for all other observables to takeUntil based on.
         */
        this._destroySubject = new Subject();
    }
    /**
     * Completes the active subject, signalling to all other observables to complete.
     * @return {?}
     */
    BreakpointObserver.prototype.ngOnDestroy = function () {
        this._destroySubject.next();
        this._destroySubject.complete();
    };
    /**
     * Whether the query currently is matched.
     * @param {?} value
     * @return {?}
     */
    BreakpointObserver.prototype.isMatched = function (value) {
        var _this = this;
        var /** @type {?} */ queries = coerceArray(value);
        return queries.some(function (mediaQuery) { return _this._registerQuery(mediaQuery).mql.matches; });
    };
    /**
     * Gets an observable of results for the given queries that will emit new results for any changes
     * in matching of the given queries.
     * @param {?} value
     * @return {?}
     */
    BreakpointObserver.prototype.observe = function (value) {
        var _this = this;
        var /** @type {?} */ queries = coerceArray(value);
        var /** @type {?} */ observables = queries.map(function (query) { return _this._registerQuery(query).observable; });
        return combineLatest(observables, function (a, b) {
            return {
                matches: !!((a && a.matches) || (b && b.matches)),
            };
        });
    };
    /**
     * Registers a specific query to be listened for.
     * @param {?} query
     * @return {?}
     */
    BreakpointObserver.prototype._registerQuery = function (query) {
        var _this = this;
        // Only set up a new MediaQueryList if it is not already being listened for.
        if (this._queries.has(query)) {
            return ((this._queries.get(query)));
        }
        var /** @type {?} */ mql = this.mediaMatcher.matchMedia(query);
        // Create callback for match changes and add it is as a listener.
        var /** @type {?} */ queryObservable = RxChain.from(fromEventPattern(
        // Listener callback methods are wrapped to be placed back in ngZone. Callbacks must be placed
        // back into the zone because matchMedia is only included in Zone.js by loading the
        // webapis-media-query.js file alongside the zone.js file.  Additionally, some browsers do not
        // have MediaQueryList inherit from EventTarget, which causes inconsistencies in how Zone.js
        // patches it.
        function (listener) {
            mql.addListener(function (e) { return _this.zone.run(function () { return listener(e); }); });
        }, function (listener) {
            mql.removeListener(function (e) { return _this.zone.run(function () { return listener(e); }); });
        }))
            .call(takeUntil, this._destroySubject)
            .call(startWith, mql)
            .call(map, function (nextMql) { return ({ matches: nextMql.matches }); })
            .result();
        // Add the MediaQueryList to the set of queries.
        var /** @type {?} */ output = { observable: queryObservable, mql: mql };
        this._queries.set(query, output);
        return output;
    };
    BreakpointObserver.decorators = [
        { type: Injectable },
    ];
    /**
     * @nocollapse
     */
    BreakpointObserver.ctorParameters = function () { return [
        { type: MediaMatcher, },
        { type: NgZone, },
    ]; };
    return BreakpointObserver;
}());
export { BreakpointObserver };
function BreakpointObserver_tsickle_Closure_declarations() {
    /** @type {?} */
    BreakpointObserver.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    BreakpointObserver.ctorParameters;
    /**
     * A map of all media queries currently being listened for.
     * @type {?}
     */
    BreakpointObserver.prototype._queries;
    /**
     * A subject for all other observables to takeUntil based on.
     * @type {?}
     */
    BreakpointObserver.prototype._destroySubject;
    /** @type {?} */
    BreakpointObserver.prototype.mediaMatcher;
    /** @type {?} */
    BreakpointObserver.prototype.zone;
}
//# sourceMappingURL=breakpoints-observer.js.map